import { stopPropagation } from "./hooks/useEventListener";
import { VNode, h, mount, patch } from "./vnode";

export class Pagination {
  /// 当前页数，从0开始
  currentPage: number;
  /// 每页item数量
  itemsPerPage: number;
  /// page中间连续的个数
  entriesInterval: number;
  /// page边缘个数
  entriesEdge: number;
  linkTo: string;
  prevText: string;
  nextText: string;
  ellipseText: string;
  prevShowAlways: boolean;
  nextShowAlways: boolean;
  callback: (page: number) => boolean;

  totalPages: number;
  parentVNode: VNode;

  constructor(
    parentContainer: HTMLElement,
    totalItemCount: number,
    options?: Partial<{
      currentPage: number;
      itemsPerPage: number;
      entriesInterval: number;
      entriesEdge: number;
      linkTo: string;
      prevText: string;
      nextText: string;
      ellipseText: string;
      prevShowAlways: boolean;
      nextShowAlways: boolean;
      callback: (page: number) => boolean;
    }>
  ) {
    this.currentPage = options?.currentPage || 0;
    this.itemsPerPage = options?.itemsPerPage || 10;
    this.entriesInterval = options?.entriesInterval || 5;
    this.entriesEdge = options?.entriesEdge || 1;
    this.linkTo = options?.linkTo || "javascript:;";
    this.prevText = options?.prevText || "上一页";
    this.nextText = options?.nextText || "下一页";
    this.ellipseText = options?.ellipseText || "...";
    this.prevShowAlways =
      typeof options?.prevShowAlways == "boolean"
        ? options?.prevShowAlways
        : true;
    this.nextShowAlways =
      typeof options?.nextShowAlways == "boolean"
        ? options?.nextShowAlways
        : true;
    this.callback = options?.callback || ((page) => false);
    this.totalPages = Math.ceil(totalItemCount / this.itemsPerPage);
    this.parentVNode = this.createParentVNode();
    mount(this.parentVNode, parentContainer);
    this.selectPage(this.currentPage);
  }

  private createParentVNode() {
    return h("div", { class: "pagination" }, []);
  }

  private appendVNode(
    vnode: VNode,
    pageID: number,
    options?: {
      text?: string;
      classes?: string;
    }
  ) {
    pageID =
      pageID < 0 ? 0 : pageID < this.totalPages ? pageID : this.totalPages - 1;
    const text = options?.text || String(pageID + 1);
    const classes = options?.classes || "";
    (vnode.children as VNode[]).push(
      pageID == this.currentPage &&
        classes.indexOf("prev") == -1 &&
        classes.indexOf("next") == -1
        ? h("span", { class: `current ${classes}` }, text)
        : h(
            "a",
            {
              class: classes,
              href: this.linkTo.replace(/__id__/, String(pageID)),
              onClick: (evt: Event) => {
                return this.selectPage(pageID, evt);
              }
            },
            text
          )
    );
  }

  private drawLinks() {
    const newParentVNode = this.createParentVNode();

    const halfInterval = Math.ceil(this.entriesInterval / 2);
    const intervalStart =
      this.currentPage > halfInterval
        ? Math.max(
            Math.min(
              this.currentPage - halfInterval,
              this.totalPages - this.entriesInterval
            ),
            0
          )
        : 0;
    const intervalEnd =
      this.currentPage > halfInterval
        ? Math.min(this.currentPage + halfInterval, this.totalPages)
        : Math.min(this.entriesInterval, this.totalPages);

    // Generate "Previous"-Link
    if (this.prevText && (this.currentPage > 0 || this.prevShowAlways)) {
      this.appendVNode(newParentVNode, this.currentPage - 1, {
        text: this.prevText,
        classes: "prev"
      });
    }
    // Generate starting points
    if (intervalStart > 0 && this.entriesEdge > 0) {
      const end = Math.min(this.entriesEdge, intervalStart);
      for (let i = 0; i < end; i++) {
        this.appendVNode(newParentVNode, i);
      }
      if (this.entriesEdge < intervalStart && this.ellipseText) {
        (newParentVNode.children as VNode[]).push(
          h("span", {}, this.ellipseText)
        );
      }
    }
    // Generate interval links
    for (let i = intervalStart; i < intervalEnd; i++) {
      this.appendVNode(newParentVNode, i);
    }
    // Generate ending points
    if (intervalEnd < this.totalPages && this.entriesEdge > 0) {
      if (
        this.totalPages - this.entriesEdge > intervalEnd &&
        this.ellipseText
      ) {
        (newParentVNode.children as VNode[]).push(
          h("span", {}, this.ellipseText)
        );
      }
      const begin = Math.max(this.totalPages - this.entriesEdge, intervalEnd);
      for (let i = begin; i < this.totalPages; i++) {
        this.appendVNode(newParentVNode, i);
      }
    }
    // Generate "Next"-Link
    if (
      this.nextText &&
      (this.currentPage < this.totalPages - 1 || this.nextShowAlways)
    ) {
      this.appendVNode(newParentVNode, this.currentPage + 1, {
        text: this.nextText,
        classes: "next"
      });
    }
    patch(this.parentVNode, newParentVNode);
    this.parentVNode = newParentVNode;
  }

  public selectPage(pageID: number, evt?: Event): boolean {
    this.currentPage = pageID =
      pageID < 0 ? 0 : pageID < this.totalPages ? pageID : this.totalPages - 1;
    this.drawLinks();
    const continuePropagation = this.callback(pageID);
    if (evt) {
      if (!continuePropagation) {
        stopPropagation(evt);
      }
    }
    return continuePropagation;
  }

  public prevPage(): boolean {
    return this.selectPage(this.currentPage - 1);
  }

  public nextPage(): boolean {
    return this.selectPage(this.currentPage + 1);
  }
}
